import React from 'react'
import { useAppSelector } from "../../../hooks/useAppSelector"
import { ResultsGridDataCol } from './ResultsGridColData'
import { ResultsGridViewCol } from './ResultsGridColView'
import { DataGrid } from 'devextreme-react'
import { Column, LoadPanel } from 'devextreme-react/data-grid'

import './ResultGrid.css'
import { Col, Pagination, PaginationItem, PaginationLink, Row, Button } from 'reactstrap'
import { useDispatch } from 'react-redux'
import { searchDataThunk } from '../../../redux/slices'
import { useTimezone } from '../../../hooks/useTimezone'

export const ResultsGrid: React.FC = () => {
    const data = useAppSelector(state => state.data.data)
    const prevOrder = useAppSelector(state => state.data.lastRequest?.order)
    const prevSort = useAppSelector(state => state.data.lastRequest?.sort)
    const groupList = useAppSelector(state => state.data.groups)
    const { formatLocalTimeFromUtc } = useTimezone()
    const dispatch = useDispatch()

    if(data === null) {
        return <></>
    }

    const onOptionChanged = (e) => {
        switch(e.fullName) {
            case 'columns[1].sortOrder':
                dispatch(searchDataThunk({ sort: 'startDate', order:  e.value }))
                break
            case 'columns[2].sortOrder':
                dispatch(searchDataThunk({ sort: 'endDate', order:  e.value }))
                break
            case 'columns[3].sortOrder':
                dispatch(searchDataThunk({ sort: 'vehicle.name', order:  e.value }))
                break
            case 'columns[4].sortOrder':
                dispatch(searchDataThunk({ sort: 'displayName', order:  e.value }))
                break
            case 'columns[5].sortOrder':
                dispatch(searchDataThunk({ sort: 'uploadId', order:  e.value }))
                break
            default:
                break;
        }
    }

    const renderTimeCol = (d: Partial<{value: string}>) => {
        return formatLocalTimeFromUtc(d.value || null)
    }

    return (
        <>
        <div className="dataset-display" id='datasetGroup'>
            <div id="dataGridParent">
            <DataGrid
                id="gridContainer"
                dataSource={data}

                showRowLines={true}
                showBorders={true}
                rowAlternationEnabled={true}
                columnAutoWidth={true}
                allowColumnResizing={true}
                onOptionChanged={onOptionChanged}
                paging={{
                    enabled: false
                }}
            >
                <LoadPanel enabled />
                <Column
                    dataField='groupId'
                    caption='Group'
                    cssClass='dataset-group-id'
                    groupIndex={0}
                    groupCellTemplate={(a, d) => {
                        const key = d.key
                        const data = d.totalItem.data
                        const a1 = data.collapsedItems || []
                        const a2 = data.items || []
                        const items = [...a1, ...a2]
    
                        if (items) {
                            const collections = items.reduce(
                            (
                            a: {
                                [key: string]: { name: string; count: number }
                            },
                            i
                            ) => {
                            if (!(i.collection in a)) {
                                a[i.collection] = { name: i.collection, count: 1 }
                            } else {
                                a[i.collection].count++
                            }
                            return a
                            },
                            {}
                        )
    
                        const collectionStr = Object.keys(collections).reduce(
                            (a, i) => {
                            const t = collections[i]
                            return `${a} ${t.name} (${t.count})`
                            },
                            ''
                        )
                        a.append(`Group ID: ${groupList[key]} - ${collectionStr}`)
                        }
                    }}
                    allowSorting={false}
                />
                <Column
                    dataField='startDate'
                    caption='Start'
                    minWidth='calc(100%/7)'
                    width='fit-content'
                    cellRender={renderTimeCol}
                    defaultSortIndex={prevSort === 'startDate' ? 0: undefined}
                    defaultSortOrder={prevSort === 'startDate' && prevOrder ? prevOrder: undefined}
                />
                <Column
                    dataField='endDate'
                    caption='End'
                    minWidth='calc(100%/7)'
                    width='fit-content'
                    cellRender={renderTimeCol}
                    defaultSortIndex={prevSort === 'endDate' ? 0: undefined}
                    defaultSortOrder={prevSort === 'endDate' && prevOrder ? prevOrder: undefined}
                />
                <Column
                    dataField='vehicle.name'
                    caption='Vehicle'
                    minWidth='calc(100%/7)'
                    width='fit-content'
                    defaultSortIndex={prevSort === 'vehicle.name' ? 0: undefined}
                    defaultSortOrder={prevSort === 'vehicle.name' && prevOrder ? prevOrder: undefined}
                />
                <Column
                    dataField='displayName'
                    caption='Name'
                    minWidth='calc(100%/7)'
                    width='fit-content'
                    defaultSortIndex={prevSort === 'displayName' ? 0: undefined}
                    defaultSortOrder={prevSort === 'displayName' && prevOrder ? prevOrder: undefined}
                />
                <Column
                    dataField='uploadId'
                    caption='Upload Id'
                    alignment='left'
                    minWidth='calc(100%/7)'
                    width='fit-content'
                    defaultSortIndex={prevSort === 'uploadId' ? 0: undefined}
                    defaultSortOrder={prevSort === 'uploadId' && prevOrder ? prevOrder: undefined}
                />
                <Column
                    dataField='versions'
                    caption='View'
                    cellRender={ResultsGridViewCol}
                    minWidth='fit-content'
                    width='auto'
                    allowSorting={false}
                />
                <Column
                    dataField='versions'
                    caption='Data Type'
                    cellRender={ResultsGridDataCol}
                    minWidth='fit-content'
                    width='auto'
                    allowSorting={false}
                />
            </DataGrid>
            </div>
        </div>
        <_ResultGridPagerOldStyle />
        </>
    )
}

export const _ResultGridPager: React.FC = () => {
    //todo - can move this to an ICS component... not sure if I like this better than the OldStyle
    const pager = useAppSelector(state => state.data.paging)
    const dispatch = useDispatch()
    
    if(pager === null) {
        return <></>
    }

    const totalPages = Math.ceil(pager.total / pager.max)
    const currentPage = Math.ceil((pager.offset + 1) / pager.max)
    const pages = Array.from({ length: totalPages }, (value, index) => index + 1)


    const onNewPage = (page: number) => {
        if(currentPage !== page) {
            const newOffset = (page - 1) * pager.max
            dispatch(searchDataThunk({offset: newOffset }))
        }
    }


    return (
        <Row>
            <Col>
                <Pagination>
                    <PaginationItem disabled={currentPage === 1 ? true : undefined} >
                        <PaginationLink first onClick={()=> onNewPage(1)} />
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === 1 ? true : undefined} >
                        <PaginationLink previous onClick={()=> onNewPage(currentPage - 1)} />
                    </PaginationItem>
                    {pages.map(i => {
                        return (
                            <PaginationItem active={i === currentPage}>
                                <PaginationLink onClick={()=> onNewPage(i)}>{i}</PaginationLink>
                            </PaginationItem>
                        )
                    })}
                    <PaginationItem disabled={currentPage === totalPages ? true : undefined} >
                        <PaginationLink next onClick={()=> onNewPage(currentPage + 1)} />
                    </PaginationItem>
                    <PaginationItem disabled={currentPage === totalPages ? true : undefined} >
                        <PaginationLink last onClick={()=> onNewPage(totalPages)} />
                    </PaginationItem>
                </Pagination>
            </Col>
        </Row>
    )
}

export const _ResultGridPagerOldStyle: React.FC = () => {
    //const pager = {total: 100, offset: 0, max: 10} //testing
    const pager = useAppSelector(state => state.data.paging)
    const dispatch = useDispatch()
    
    if(pager === null) {
        return <></>
    }

    const totalPages = Math.ceil(pager.total / pager.max)
    const currentPage = Math.ceil((pager.offset + 1) / pager.max)
    const pages = Array.from({ length: totalPages }, (value, index) => index + 1)


    const onNewPage = (page: number | string) => {
        if(typeof page === 'string') {
            page = parseInt(page)
        }

        if(page && currentPage !== page) {
            const newOffset = (page - 1) * pager.max
            dispatch(searchDataThunk({offset: newOffset }))
        }
    }


    return (
        <Row className='ResultGridPager mt-3 mb-4'>
            <Col className='text-left p-0'>
                <Button className='mr-1' color='primary' disabled={currentPage === 1 ? true : undefined} onClick={() => onNewPage(1)}>
                    <span className="glyphicons glyphicons-fast-backward" style={{float: 'left'}}></span> First
                </Button>
                <Button className='mr-1' color='primary' disabled={currentPage === 1 ? true : undefined} onClick={() => onNewPage(currentPage - 1)}>
                    <span className="glyphicons glyphicons-rewind" style={{float: 'left'}}></span> Prev
                </Button>
            </Col>
            <Col className='text-center'>
                Page
                <input
                    style={{width: '50px'}}
                    className='input-sm' defaultValue={currentPage}
                    onBlur={(e) => onNewPage(e.target.value)}
                 />
                 of {totalPages}
            </Col>
            <Col className='text-right p-0'>
                <Button color='primary' className='ml-1' disabled={currentPage === totalPages ? true : undefined} onClick={() => onNewPage(currentPage + 1)}>
                    <span className="glyphicons glyphicons-forward" style={{float: 'right'}}></span> Next
                </Button>
                <Button color='primary' className='ml-1' disabled={currentPage === totalPages ? true : undefined} onClick={() => onNewPage(totalPages)}>
                    <span className="glyphicons glyphicons-fast-forward" style={{float: 'right'}}></span> Last
                </Button>
            </Col>
        </Row>
    )
}
